// ============================================================================
// TRAFFIC SIMULATION TYPES
// Types for graph-based traffic flow simulation
// ============================================================================

import type { UUID, Protocol } from './types';

// ============================================================================
// TRAFFIC FLOW TYPES
// ============================================================================

export type TrafficType = 'read' | 'write' | 'replication' | 'async' | 'health_check';

export type NodeRole =
  | 'entry_point'    // Client, API Gateway
  | 'load_balancer'  // LB, reverse proxy
  | 'compute'        // App server, worker
  | 'cache'          // Redis, Memcached
  | 'database'       // Primary data store
  | 'queue'          // Message queue, event bus
  | 'cdn'            // CDN, edge cache
  | 'storage';       // Object storage, file system

export type TrafficSplitMode =
  | 'round_robin'
  | 'weighted'
  | 'random'
  | 'least_connections'
  | 'consistent_hash';

// ============================================================================
// NODE TRAFFIC CONFIGURATION
// ============================================================================

export interface NodeTrafficConfig {
  nodeId: string;
  role: NodeRole;
  maxQps: number;              // Maximum queries per second
  currentQps: number;          // Current load
  readQps: number;             // Read traffic
  writeQps: number;            // Write traffic
  baseLatencyMs: number;       // Processing latency
  latencyUnderLoad: number;    // Latency when near capacity
  failureRate: number;         // 0.0-1.0, for chaos simulation
  cacheHitRate: number;        // For cache nodes, 0.0-1.0
  trafficSplitMode: TrafficSplitMode;
  splitWeights: Record<string, number>; // target_id -> weight
  isEnabled: boolean;          // For failure simulation
  isReplica: boolean;          // Read replica, follower
  replicationLagMs: number;
}

// ============================================================================
// EDGE TRAFFIC CONFIGURATION
// ============================================================================

export interface EdgeTrafficConfig {
  edgeId: string;
  sourceId: string;
  targetId: string;
  trafficTypes: TrafficType[]; // What types of traffic flow here
  latencyOverhead: number;     // Network hop latency (ms)
  bandwidthMbps: number;       // Max throughput
  isEnabled: boolean;          // For failure simulation
  isAsync: boolean;            // Async communication
  protocol: Protocol;
}

// ============================================================================
// TRAFFIC FLOW
// ============================================================================

export interface TrafficFlow {
  flowId: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeId: string;
  trafficType: TrafficType;
  qps: number;
  latencyMs: number;           // Cumulative latency
  timestamp: number;           // For animation keyframes
  dropped: boolean;            // If traffic was dropped due to capacity
  dropReason?: string;
}

// ============================================================================
// SIMULATION STEP (for animation)
// ============================================================================

export interface SimulationStep {
  stepNumber: number;
  nodeId: string;
  action: 'receive' | 'process' | 'forward' | 'drop' | 'cache_hit' | 'cache_miss';
  incomingQps: number;
  outgoingQps: number;
  latencyMs: number;
  flows: TrafficFlow[];
}

// ============================================================================
// BOTTLENECK INFO
// ============================================================================

export interface BottleneckInfo {
  nodeId: string;
  nodeLabel: string;
  utilization: number;         // Current utilization 0.0-1.0
  maxQps: number;
  currentQps: number;
  excessQps: number;           // QPS above capacity
  severity: 'warning' | 'critical' | 'overflow';
  recommendation: string;
}

// ============================================================================
// PATH INFO
// ============================================================================

export interface PathInfo {
  pathId: string;
  pathNodes: string[];
  trafficType: TrafficType;
  totalLatency: number;
  qps: number;
  isHealthy: boolean;
}

// ============================================================================
// SIMULATION STATE
// ============================================================================

export interface SimulationState {
  // Traffic flows
  flows: TrafficFlow[];
  steps: SimulationStep[];

  // Node states
  nodeUtilization: Record<string, number>;  // node_id -> 0.0-1.0
  nodeLatencies: Record<string, number>;    // node_id -> cumulative latency
  nodeQps: Record<string, number>;          // node_id -> current QPS

  // Bottlenecks and issues
  bottlenecks: BottleneckInfo[];
  failedNodes: string[];
  droppedTrafficPct: number;    // Percentage of traffic dropped
  droppedQps: number;           // Absolute QPS dropped

  // Latency metrics
  totalLatencyMs: number;       // End-to-end latency
  p50LatencyMs: number;
  p99LatencyMs: number;

  // Path analysis
  readPathLatency: number;
  writePathLatency: number;
  criticalPath: string[];       // Slowest path through system
  pathsAnalyzed: PathInfo[];

  // Summary
  totalQpsProcessed: number;
  systemHealthy: boolean;
  healthScore: number;          // 0-100
}

// ============================================================================
// FAILURE SCENARIOS (Chaos Engineering)
// ============================================================================

export interface FailureScenario {
  id: string;
  name: string;
  description: string;
  failNodes: string[];          // Node IDs to disable
  failEdges: string[];          // Edge IDs to disable
  failureType: 'node_failure' | 'network_partition' | 'degraded';
  severity: 'minor' | 'major' | 'catastrophic';
}

export interface ChaosResult {
  scenario: FailureScenario;
  beforeState: SimulationState | null;
  afterState: SimulationState | null;
  systemSurvived: boolean;      // Traffic still reaches data stores
  partialOutage: boolean;       // Some traffic lost
  totalOutage: boolean;         // No traffic reaches destination
  redundancyUsed: boolean;      // Failover activated
  alternatePathsUsed: string[][]; // Backup paths taken
  droppedTrafficPct: number;    // % traffic lost
  latencyIncrease: number;      // % increase in latency
  recoveryPossible: boolean;    // Can system auto-recover
  recommendations: string[];
  resilienceScore: number;      // 0-100
}

// ============================================================================
// SPOF ANALYSIS
// ============================================================================

export interface SPOFAnalysis {
  nodeId: string;
  nodeLabel: string;
  isSPOF: boolean;
  failureImpact: 'none' | 'partial' | 'complete';
  trafficAffectedPct: number;
  chaosResult?: ChaosResult;
  mitigationAdvice: string;
}

// ============================================================================
// SIMULATION CONFIGURATION
// ============================================================================

export interface SimulationConfig {
  // Traffic configuration
  entryTraffic: Record<string, number>;  // entry_node -> QPS
  readWriteRatio: number;                // 0.0-1.0, ratio of reads
  trafficPattern: 'steady' | 'spike' | 'gradual';
  spikeFactor: number;                   // Multiplier for spike traffic

  // Simulation parameters
  maxSteps: number;                      // Max BFS iterations
  timeStepMs: number;                    // Simulation time granularity
  enableAnimation: boolean;              // Generate animation keyframes

  // Chaos parameters
  enableChaos: boolean;
  chaosScenarios: FailureScenario[];
  testAllSpofs: boolean;
}

// ============================================================================
// API REQUEST/RESPONSE
// ============================================================================

export interface SimulationRequest {
  designJson: unknown;
  config: SimulationConfig;
  edgeConfigs?: Record<string, EdgeTrafficConfig>; // User-defined edge types
}

export interface SimulationResponse {
  state: SimulationState;
  chaosResults?: ChaosResult[];
  spofAnalysis?: SPOFAnalysis[];
  animationFrames?: SimulationStep[];
  recommendations: string[];
  overallScore: number;
  simulationTimeMs: number;
}

// ============================================================================
// PREDEFINED FAILURE SCENARIOS
// ============================================================================

export const PREDEFINED_CHAOS_SCENARIOS: FailureScenario[] = [
  {
    id: 'database_failure',
    name: 'Primary Database Failure',
    description: 'Simulates the primary database becoming unavailable',
    failNodes: [],
    failEdges: [],
    failureType: 'node_failure',
    severity: 'catastrophic',
  },
  {
    id: 'cache_failure',
    name: 'Cache Layer Failure',
    description: 'Simulates cache becoming unavailable, causing cache stampede',
    failNodes: [],
    failEdges: [],
    failureType: 'node_failure',
    severity: 'major',
  },
  {
    id: 'load_balancer_failure',
    name: 'Load Balancer Failure',
    description: 'Simulates primary load balancer failure',
    failNodes: [],
    failEdges: [],
    failureType: 'node_failure',
    severity: 'catastrophic',
  },
  {
    id: 'single_server_failure',
    name: 'Single Application Server Failure',
    description: 'Simulates one application server going down',
    failNodes: [],
    failEdges: [],
    failureType: 'node_failure',
    severity: 'minor',
  },
  {
    id: 'network_partition',
    name: 'Network Partition',
    description: 'Simulates network split between components',
    failNodes: [],
    failEdges: [],
    failureType: 'network_partition',
    severity: 'major',
  },
  {
    id: 'degraded_database',
    name: 'Degraded Database Performance',
    description: 'Database responding slowly (10x latency)',
    failNodes: [],
    failEdges: [],
    failureType: 'degraded',
    severity: 'major',
  },
  {
    id: 'cdn_failure',
    name: 'CDN Failure',
    description: 'CDN becomes unavailable, all requests hit origin',
    failNodes: [],
    failEdges: [],
    failureType: 'node_failure',
    severity: 'major',
  },
  {
    id: 'queue_failure',
    name: 'Message Queue Failure',
    description: 'Message queue becomes unavailable',
    failNodes: [],
    failEdges: [],
    failureType: 'node_failure',
    severity: 'major',
  },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Maps component category to node role for traffic simulation
 */
export function nodeRoleFromCategory(category: string): NodeRole {
  switch (category.toLowerCase()) {
    case 'client':
      return 'entry_point';
    case 'load_balancer':
    case 'loadbalancer':
    case 'reverse_proxy':
    case 'api_gateway':
    case 'gateway':
      return 'load_balancer';
    case 'compute':
    case 'application':
    case 'serverless':
    case 'container':
      return 'compute';
    case 'cache':
    case 'in_memory_cache':
      return 'cache';
    case 'database':
    case 'sql':
    case 'nosql':
    case 'graph_db':
    case 'time_series':
      return 'database';
    case 'queue':
    case 'message_broker':
    case 'event_bus':
    case 'streaming':
      return 'queue';
    case 'cdn':
    case 'edge':
      return 'cdn';
    case 'storage':
    case 'object_storage':
    case 'file_storage':
    case 'blob':
      return 'storage';
    default:
      return 'compute';
  }
}

/**
 * Returns default traffic config for a node role
 */
export function defaultNodeTrafficConfig(role: NodeRole): Partial<NodeTrafficConfig> {
  const baseConfig: Partial<NodeTrafficConfig> = {
    role,
    isEnabled: true,
    currentQps: 0,
    readQps: 0,
    writeQps: 0,
    failureRate: 0,
    isReplica: false,
    replicationLagMs: 0,
    splitWeights: {},
    trafficSplitMode: 'round_robin',
  };

  switch (role) {
    case 'entry_point':
      return {
        ...baseConfig,
        maxQps: 1000000,
        baseLatencyMs: 1,
        latencyUnderLoad: 5,
        cacheHitRate: 0,
      };
    case 'load_balancer':
      return {
        ...baseConfig,
        maxQps: 100000,
        baseLatencyMs: 1,
        latencyUnderLoad: 5,
        cacheHitRate: 0,
      };
    case 'compute':
      return {
        ...baseConfig,
        maxQps: 5000,
        baseLatencyMs: 10,
        latencyUnderLoad: 50,
        cacheHitRate: 0,
      };
    case 'cache':
      return {
        ...baseConfig,
        maxQps: 100000,
        baseLatencyMs: 1,
        latencyUnderLoad: 5,
        cacheHitRate: 0.8,
      };
    case 'database':
      return {
        ...baseConfig,
        maxQps: 10000,
        baseLatencyMs: 5,
        latencyUnderLoad: 100,
        cacheHitRate: 0,
      };
    case 'queue':
      return {
        ...baseConfig,
        maxQps: 50000,
        baseLatencyMs: 2,
        latencyUnderLoad: 10,
        cacheHitRate: 0,
      };
    case 'cdn':
      return {
        ...baseConfig,
        maxQps: 500000,
        baseLatencyMs: 5,
        latencyUnderLoad: 20,
        cacheHitRate: 0.95,
      };
    case 'storage':
      return {
        ...baseConfig,
        maxQps: 5000,
        baseLatencyMs: 50,
        latencyUnderLoad: 200,
        cacheHitRate: 0,
      };
    default:
      return {
        ...baseConfig,
        maxQps: 10000,
        baseLatencyMs: 10,
        latencyUnderLoad: 50,
        cacheHitRate: 0,
      };
  }
}

/**
 * Calculate utilization color based on percentage
 */
export function utilizationColor(utilization: number): string {
  if (utilization < 0.5) return '#22c55e';      // green
  if (utilization < 0.7) return '#eab308';      // yellow
  if (utilization < 0.9) return '#f97316';      // orange
  return '#ef4444';                              // red
}

/**
 * Get severity color for bottleneck
 */
export function bottleneckSeverityColor(severity: BottleneckInfo['severity']): string {
  switch (severity) {
    case 'warning': return '#eab308';
    case 'critical': return '#f97316';
    case 'overflow': return '#ef4444';
    default: return '#6b7280';
  }
}

/**
 * Format QPS for display
 */
export function formatQps(qps: number | undefined | null): string {
  if (qps == null) return '0';
  if (qps >= 1000000) return `${(qps / 1000000).toFixed(1)}M`;
  if (qps >= 1000) return `${(qps / 1000).toFixed(1)}K`;
  return qps.toFixed(0);
}

/**
 * Calculate health score color
 */
export function healthScoreColor(score: number): string {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#eab308';
  if (score >= 40) return '#f97316';
  return '#ef4444';
}
